"use client";

import * as React from "react";
import { Terminal } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqs = [{
  question: "What kind of computer repair services do you offer?",
  answer: "I handle everything from hardware diagnostics and part replacements to virus removal, OS optimization, and custom PC builds. If it computes, I can fix it."
}, {
  question: "How long does a typical repair take?",
  answer: "Most standard repairs, such as part replacements or virus removals, are completed within 24-48 hours. Complex diagnostics or custom builds may take slightly longer depending on part availability."
}, {
  question: "Can you build custom PCs or upgrade my current one?",
  answer: "Absolutely. I specialize in custom rigs for gaming, workstations, and general use. I can also evaluate your current system and recommend targeted upgrades to boost performance."
}, {
  question: "What should I do before bringing my computer in?",
  answer: "If possible, back up your important data to an external drive or cloud storage. Please bring your power cable (especially for laptops) and any specific software disks if relevant to the issue."
}, {
  question: "Do you repair laptops as well as desktops?",
  answer: "Yes, I service both laptops and desktops. This includes screen replacements, battery swaps, keyboard repairs, and internal component troubleshooting for most major brands."
}, {
  question: "Do you offer data recovery services?",
  answer: "Yes, I can recover data from failing hard drives, accidental deletions, or corrupted operating systems. I also provide advice on setting up automated backup solutions to prevent future data loss."
}, {
  question: "Is there a diagnostic fee?",
  answer: "I offer a flat-rate diagnostic service. If you choose to proceed with the recommended repair, the diagnostic fee is applied toward the total cost of the service."
}, {
  question: "Do you provide on-site repairs or pick-up services?",
  answer: "Yes — I offer on-site repairs for certain issues, depending on the type of device and the work needed. For more involved repairs, I complete the work in my dedicated repair lab in Pflugerville, where I have access to specialized tools and equipment. I also provide convenient local pick-up and drop-off services."
}, {
  question: "What kind of warranty do you provide on repairs?",
  answer: "All hardware repairs come with a 90-day warranty on labor, plus whatever manufacturer warranty applies to the new parts installed. Your satisfaction and system stability are my top priorities."
}];
export default function TerminalFaqAccordion() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="terminalfaqaccordion" className="bg-background py-16 md:py-24 font-mono relative overflow-hidden scroll-mt-20">
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none"></div>

      <div ref={ref} className={`container mx-auto px-4 sm:px-6 md:px-8 lg:px-12 max-w-3xl relative z-10 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="mb-12 text-center md:text-left">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4 flex items-center justify-center md:justify-start gap-3">
            <Terminal className="size-8 md:size-10" />
            <span>&gt; SYSTEM_FAQ.exe</span>
            <span className="animate-pulse inline-block overflow-visible whitespace-nowrap">_</span>
          </h2>
          <p className="text-muted-foreground text-sm md:text-base">
            Accessing knowledge base... {faqs.length} records found.
          </p>
        </div>

        <Accordion type="single" collapsible className="w-full space-y-4 md:space-y-6">
          {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i} className="border border-primary/20 bg-card/40 rounded-md px-4 sm:px-6 md:px-8 data-[state=open]:border-primary/60 data-[state=open]:bg-card/80 transition-all duration-300 shadow-sm">
              <AccordionTrigger className="text-primary hover:text-primary/80 hover:no-underline py-5 text-left font-semibold text-sm sm:text-base md:text-lg lg:text-xl group">
                <span className="flex gap-3 items-start">
                  <span className="text-primary/60 shrink-0 font-bold group-hover:text-primary transition-colors">
                    &gt;Q:
                  </span>
                  <span>{faq.question}</span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground pb-5 pt-2 border-t border-primary/10 text-sm sm:text-base md:text-lg leading-relaxed">
                <span className="flex gap-3 items-start">
                  <span className="text-primary/40 shrink-0 font-bold">
                    &gt;A:
                  </span>
                  <span className="font-body-sans">{faq.answer}</span>
                </span>
              </AccordionContent>
            </AccordionItem>)}
        </Accordion>
      </div>
    </section>;
}